import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Skeleton,
} from '@repo/ui'
import { createFileRoute } from '@tanstack/react-router'
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Plus,
  RotateCw,
  Tag,
  X,
} from 'lucide-react'
import { Fragment, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { AddLeadToCadenceDialog } from '@/components/cadences/AddLeadToCadenceDialog'
import { BulkAddByTagDialog } from '@/components/cadences/BulkAddByTagDialog'
import { EnrollmentLogsTimeline } from '@/components/cadences/EnrollmentLogsTimeline'
import { useAuth } from '@/hooks/use-auth'
import {
  useCadence,
  useCadenceBlocks,
  useCadenceEnrollments,
  useCancelEnrollment,
  useRetryEnrollment,
} from '@/hooks/use-cadences'
import { useChannels } from '@/hooks/use-channels'
import type {
  CadenceBlockSource,
  CadenceEnrollment,
  CadenceEnrollmentStatus,
} from '@/types/cadences'

export const Route = createFileRoute('/_app/cadencias/$cadenceId/leads')({
  component: CadenceLeadsPage,
})

const STATUS_LABEL: Record<CadenceEnrollmentStatus, string> = {
  ACTIVE: 'Em andamento',
  PAUSED: 'Pausado',
  COMPLETED: 'Concluido',
  FAILED: 'Falhou',
  CANCELLED: 'Cancelado',
}

const STATUS_VARIANT: Record<
  CadenceEnrollmentStatus,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  ACTIVE: 'default',
  PAUSED: 'secondary',
  COMPLETED: 'outline',
  FAILED: 'destructive',
  CANCELLED: 'outline',
}

const BLOCK_SOURCE_LABEL: Record<CadenceBlockSource, string> = {
  MANUAL: 'Manual',
  TAG: 'Tag',
  RD_WEBHOOK: 'RD Station',
  INBOUND_WEBHOOK: 'Webhook',
  AI_AGENT: 'IA',
  BACKFILL: 'Backfill',
}

const PAGE_SIZE = 20

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function CadenceLeadsPage() {
  const { cadenceId } = Route.useParams()
  const { currentOrgId } = useAuth()
  const [addOpen, setAddOpen] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [page, setPage] = useState(1)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [cancelTarget, setCancelTarget] = useState<CadenceEnrollment | null>(
    null
  )
  const { data: cadence } = useCadence(currentOrgId, cadenceId)
  const { data: channels = [] } = useChannels(currentOrgId)
  const cadenceInactive = cadence?.status !== 'ACTIVE'
  const {
    data: response,
    isLoading,
    refetch,
  } = useCadenceEnrollments(currentOrgId, cadenceId, {
    page,
    limit: PAGE_SIZE,
  })
  const cancelEnrollment = useCancelEnrollment(currentOrgId, cadenceId)
  const retryEnrollment = useRetryEnrollment(currentOrgId, cadenceId)
  const enrollments = response?.data ?? []
  const total = response?.meta?.total ?? response?.total ?? 0
  const limit = response?.meta?.limit ?? response?.limit ?? PAGE_SIZE
  const totalPages = Math.max(
    1,
    response?.meta?.totalPages ?? Math.ceil(total / limit)
  )
  const { data: blocksResponse } = useCadenceBlocks(currentOrgId, cadenceId)
  const blocks = blocksResponse?.data ?? []

  const hasConnectedChannel = useMemo(() => {
    if (!cadence || cadence.channel_ids.length === 0) return false
    return channels.some(
      (channel) =>
        cadence.channel_ids.includes(channel.id) &&
        channel.status === 'CONNECTED'
    )
  }, [cadence, channels])

  function toggleExpanded(id: string) {
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function cancel() {
    if (!cancelTarget) return
    await cancelEnrollment.mutateAsync(cancelTarget.id)
    toast.success('Inscrição cancelada')
    setCancelTarget(null)
  }

  async function retry(enrollment: CadenceEnrollment) {
    await retryEnrollment.mutateAsync(enrollment.id)
    toast.success('Inscrição reenviada para a fila')
  }

  return (
    <div className="space-y-4">
      <section className="bg-card border-border flex flex-wrap items-start justify-between gap-4 rounded-xl border p-5">
        <div>
          <h2 className="text-base font-semibold">Leads inscritos</h2>
          <p className="text-muted-foreground mt-1 text-sm">
            {total} {total === 1 ? 'lead' : 'leads'} nesta cadência.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setBulkOpen(true)}
            disabled={cadenceInactive}
          >
            <Tag className="h-4 w-4" />
            Adicionar por tag
          </Button>
          <Button
            type="button"
            onClick={() => setAddOpen(true)}
            disabled={cadenceInactive}
          >
            <Plus className="h-4 w-4" />
            Adicionar lead
          </Button>
        </div>
      </section>

      {cadenceInactive ? (
        <div className="border-warning/30 bg-warning/10 text-warning flex items-center gap-2 rounded-md border px-3 py-2 text-xs">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          Ative a cadência para poder adicionar leads.
        </div>
      ) : null}

      {!hasConnectedChannel ? (
        <div className="border-destructive/40 bg-destructive/10 text-destructive flex items-center gap-2 rounded-md border px-3 py-2 text-xs">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          Esta cadência não possui canal conectado. As inscrições podem ser
          criadas, mas o envio depende da configuração do canal.
        </div>
      ) : null}

      {isLoading ? (
        <Skeleton className="h-80 w-full rounded-md" />
      ) : enrollments.length === 0 ? (
        <EmptyState
          title="Nenhum lead inscrito ainda"
          description="Adicione leads manualmente ou por tag para iniciar a sequência."
          action={
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setAddOpen(true)}
              disabled={cadenceInactive}
            >
              <Plus className="h-4 w-4" />
              Adicionar lead
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          <div className="bg-card border-border overflow-hidden rounded-xl border">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground text-xs">
                <tr>
                  <th className="w-8" />
                  <th className="px-4 py-2 text-left font-medium">Lead</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                  <th className="px-4 py-2 text-left font-medium">
                    Etapa atual
                  </th>
                  <th className="px-4 py-2 text-left font-medium">
                    Próximo envio
                  </th>
                  <th className="px-4 py-2 text-left font-medium">Motivo</th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {enrollments.map((enrollment) => {
                  const isExpanded = expanded.has(enrollment.id)
                  return (
                    <Fragment key={enrollment.id}>
                      <tr className="hover:bg-muted/20">
                        <td className="px-2 py-2.5">
                          <button
                            type="button"
                            onClick={() => toggleExpanded(enrollment.id)}
                            className="text-muted-foreground hover:bg-muted hover:text-foreground flex h-6 w-6 items-center justify-center rounded-md"
                            title={isExpanded ? 'Ocultar logs' : 'Ver logs'}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-3.5 w-3.5" />
                            ) : (
                              <ChevronRight className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-2.5">
                          <div
                            className="max-w-70 truncate font-medium"
                            title={
                              enrollment.contact?.name ?? enrollment.contactId
                            }
                          >
                            {enrollment.contact?.name ?? enrollment.contactId}
                          </div>
                          <div className="text-muted-foreground max-w-70 truncate text-xs">
                            {enrollment.contact?.phone ??
                              enrollment.contact?.email ??
                              '—'}
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <Badge
                            variant={STATUS_VARIANT[enrollment.status]}
                            className="text-2xs"
                          >
                            {STATUS_LABEL[enrollment.status]}
                          </Badge>
                        </td>
                        <td className="text-muted-foreground px-4 py-2.5 text-xs">
                          Etapa {enrollment.current_step_position + 1}
                        </td>
                        <td className="text-muted-foreground px-4 py-2.5 text-xs">
                          {enrollment.next_step_at
                            ? new Date(enrollment.next_step_at).toLocaleString(
                                'pt-BR'
                              )
                            : '—'}
                        </td>
                        <td className="text-muted-foreground px-4 py-2.5 text-xs">
                          {'—'}
                        </td>
                        <td className="px-2 py-2.5 text-right">
                          <div className="flex justify-end gap-1">
                            {enrollment.status === 'FAILED' ? (
                              <Button
                                type="button"
                                size="icon"
                                variant="ghost"
                                onClick={() => void retry(enrollment)}
                                title="Tentar novamente"
                                disabled={retryEnrollment.isPending}
                              >
                                <RotateCw className="h-3.5 w-3.5" />
                              </Button>
                            ) : null}
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              disabled={enrollment.status === 'CANCELLED'}
                              onClick={() => setCancelTarget(enrollment)}
                              title="Cancelar"
                            >
                              <X className="text-destructive h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded ? (
                        <tr className="bg-muted/10">
                          <td />
                          <td colSpan={6} className="px-2 pb-3">
                            <div className="bg-background border-border rounded-md border p-4">
                              <EnrollmentLogsTimeline
                                logs={enrollment.logs ?? []}
                              />
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div className="flex items-center justify-between gap-4">
              <p className="text-muted-foreground text-sm">
                {(page - 1) * limit + 1}-{Math.min(page * limit, total)} de{' '}
                {total}
              </p>
              <Pagination className="mx-0 w-auto">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      aria-disabled={page <= 1}
                      className={
                        page <= 1 ? 'pointer-events-none opacity-50' : ''
                      }
                      onClick={(event) => {
                        event.preventDefault()
                        setExpanded(new Set())
                        setPage((current) => Math.max(1, current - 1))
                      }}
                    />
                  </PaginationItem>
                  {Array.from({ length: totalPages })
                    .map((_, index) => index + 1)
                    .filter(
                      (pageNumber) =>
                        pageNumber === 1 ||
                        pageNumber === totalPages ||
                        Math.abs(pageNumber - page) <= 1
                    )
                    .map((pageNumber) => (
                      <PaginationItem key={pageNumber}>
                        <PaginationLink
                          isActive={pageNumber === page}
                          onClick={(event) => {
                            event.preventDefault()
                            setExpanded(new Set())
                            setPage(pageNumber)
                          }}
                        >
                          {pageNumber}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                  <PaginationItem>
                    <PaginationNext
                      aria-disabled={page >= totalPages}
                      className={
                        page >= totalPages
                          ? 'pointer-events-none opacity-50'
                          : ''
                      }
                      onClick={(event) => {
                        event.preventDefault()
                        setExpanded(new Set())
                        setPage((current) => Math.min(totalPages, current + 1))
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          ) : null}
        </div>
      )}

      {blocks.length > 0 ? (
        <section className="bg-card border-border overflow-hidden rounded-xl border">
          <div className="border-border border-b px-4 py-3">
            <h3 className="text-sm font-medium">Tentativas bloqueadas</h3>
            <p className="text-muted-foreground mt-0.5 text-xs">
              Leads que não entraram na cadência (já tinham outra cadência ativa
              ou pausada, sem telefone, etc.).
            </p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground text-xs">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Lead</th>
                <th className="px-4 py-2 text-left font-medium">Origem</th>
                <th className="px-4 py-2 text-left font-medium">Motivo</th>
                <th className="px-4 py-2 text-left font-medium">
                  Cadência ativa
                </th>
                <th className="px-4 py-2 text-left font-medium">Quando</th>
              </tr>
            </thead>
            <tbody className="divide-border divide-y">
              {blocks.map((block) => (
                <tr key={block.id}>
                  <td className="px-4 py-2">
                    <div
                      className="max-w-70 truncate"
                      title={block.contact.name ?? block.contact.phone ?? '—'}
                    >
                      {block.contact.name ?? block.contact.phone ?? '—'}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <Badge variant="outline" className="text-2xs">
                      {BLOCK_SOURCE_LABEL[block.source]}
                    </Badge>
                  </td>
                  <td className="text-muted-foreground px-4 py-2">
                    {block.reason}
                  </td>
                  <td className="text-muted-foreground px-4 py-2">
                    {block.blockingCadenceName ?? '—'}
                  </td>
                  <td className="text-muted-foreground px-4 py-2">
                    {formatDateTime(block.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      <AddLeadToCadenceDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        cadenceId={cadenceId}
        orgId={currentOrgId}
        onEnrolled={() => void refetch()}
      />
      <BulkAddByTagDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        cadenceId={cadenceId}
        orgId={currentOrgId}
        onEnrolled={() => void refetch()}
      />
      <ConfirmDialog
        open={!!cancelTarget}
        onOpenChange={(open) => !open && setCancelTarget(null)}
        title="Cancelar inscrição?"
        description="O lead deixará de receber os próximos passos desta cadência."
        confirmLabel="Cancelar inscrição"
        destructive
        cancelLabel="Voltar"
        pending={cancelEnrollment.isPending}
        onConfirm={() => void cancel()}
      />
    </div>
  )
}
